import storage from '../../utils/storage';
import Cookies from 'js-cookie';
import ChartBase from '../../chart/chart-base';

import { default as PeriodSelector, EVENTS as periodSelectorEvents, TIMEFRAMES } from '../../chart/ui/period-selector';
import { default as ChartTypeSelector, EVENTS as chartTypeSelectorEvents } from '../../chart/ui/chart-type-selector';
import { default as Legend, EVENTS as legendEvents } from '../../chart/ui/legend';

const uppercaseFirst = str => str.charAt(0).toUpperCase() + str.slice(1);

const SERIES_IDS = {
	ask: 'ask',
	barrier: 'barrier',
	bid: 'bid',
	price: 'price',
	referencePrice: 'referencePrice',
	resetThreshold: 'resetThreshold',
	strike: 'strike',
	volatility: 'volatility',
};

class ChartProducts extends ChartBase {
	constructor(el) {
		super(el);

		// Setup aditional interactive elements
		this.periodSelector = new PeriodSelector(this.refs.periodSelector);
		this.typeSelector = new ChartTypeSelector(this.refs.type);
		this.legend = new Legend(this.refs.legend);

		// Event listeners
		this.on(periodSelectorEvents.periodChange);
		this.on(chartTypeSelectorEvents.typeChange);
		this.on(legendEvents.serieToggled);
	}

	// Override
	afterRenderChart() {
		this.getChartData(this.endpoint);
	}

	// Override
	onGetChartDataSuccess(data) {
		this.addDataToChart(data);

		this.legend.series = this.chart.series;
	}

	// Override
	hasQuotes(apiResponse = []) { // eslint-disable-line
		return apiResponse.some(({ ticks = [] }) => ticks.length);
	}

	async onPeriodChange(e) {
		const { period } = e.detail;
		const isIntraday = period === TIMEFRAMES.intraday;
		const isDay = period === TIMEFRAMES.day;
		const useDateTime = isIntraday || isDay;
		const useOrdinal = !isIntraday;

		// The type selector needs to be disabled and set to line type in case of intraday period
		if (isIntraday) {
			this.typeSelector.setValue('line');
			this.typeSelector.disabled = true;
		} else {
			this.typeSelector.disabled = false;
		}

		// Some settings are period related so the chart config has to be updated
		this.updateChartConfig(useDateTime, useOrdinal, false);

		// Update chart
		this.getChartData(this.endpoint);

		// Only listen for live ticks when period is intraday
		this.toggleTickListener(isIntraday);
	}

	onTypeChange(e) {
		// Update chart
		this.getChartData(this.endpoint);
	}

	onSerieToggled(e) {
		const { detail: { serieId, isVisible } } = e;
		const series = this.chart.get(serieId);

		series.setVisible(isVisible, true);

		// Store settings in local storage
		this.storeSeriesSettings();
	}

	storePeriodAndTypeSettings() {
		const data = {
			activePeriod: this.periodSelector.activePeriod,
			activeChartType: this.typeSelector.activeChartType,
		};

		// Selected period and chart type information is stored in cookie because it's used by the server on page render
		Cookies.set(this.storageKey, JSON.stringify(data), { expires: 7 });
	}

	storeSeriesSettings() {
		const { chart: { series } } = this;
		const dataStore = {};

		series.forEach((item) => {
			dataStore[item.options.id] = {
				visible: item.visible,
			};
		});

		// Series information is saved in local storage because it's used by JavaScript
		storage.save(this.storageKey, dataStore, true);
	}

	addDataToChart(data) {
		const { chart, chart: { series } } = this;

		if (series.length) {
			this.removeAllSeries(false); // don't redraw
		}
		data
			.filter(this.shouldAddSeries, this)
			.forEach((item) => {
				const seriesConfig = this.getSeriesConfig(item);

				chart.addSeries(seriesConfig, false);
			});

		chart.redraw();

		// Store settings in cookie
		this.storePeriodAndTypeSettings();
	}

	// checks on the option `hideSerieId = true`
	// pass in the DOM as `data-option-hide-strike="true"
	shouldAddSeries(series) {
		const { id } = series;
		const idFirstUpperCase = uppercaseFirst(id);
		const optionProperty = `hide${idFirstUpperCase}`;

		const shouldAdd = !this.options.hasOwnProperty(optionProperty) || this.options[optionProperty] === false;

		return shouldAdd;
	}

	getSeriesConfig(series) {
		const { id, labelId, ticks: data } = series;

		return {
			id,
			data,
			name: this.options[`label${uppercaseFirst(labelId)}`],
			type: this.getSeriesType(id),
			visible: this.getSeriesVisibility(id),
			className: ChartProducts.getSeriesClassName(id),
			step: ChartProducts.getSeriesStep(id),
			yAxis: ChartProducts.getSeriesAxis(id),
		};
	}

	getSeriesType(id) {
		switch (id) {
			case 'price':
				return this.typeSelector.activeChartType;
			default:
				return 'line';
		}
	}

	getSeriesVisibility(id) {
		const storedData = this.storedSettings[id];

		switch (id) {
			case SERIES_IDS.bid:
			case SERIES_IDS.ask:
			case SERIES_IDS.price:
				return storedData ? storedData.visible : true;
			default:
				return storedData ? storedData.visible : false;
		}
	}

	static getSeriesAxis(id) {
		switch (id) {
			case SERIES_IDS.bid:
			case SERIES_IDS.ask:
			case SERIES_IDS.price:
				return 'y-axis-right';
			case SERIES_IDS.volatility:
				return 'y-axis-right-volatility';
			default:
				return 'y-axis-left';
		}
	}

	// https://api.highcharts.com/highcharts/plotOptions.series.step
	static getSeriesStep(id) {
		switch (id) {
			case SERIES_IDS.barrier:
			case SERIES_IDS.strike:
			case SERIES_IDS.resetThreshold:
				return 'left';
			default:
				return false;
		}
	}

	static getSeriesClassName(id) {
		const classPrefix = 'series-color-';

		// convert camelCase to kebab-case to match CSS classname conventions
		const formattedId = SERIES_IDS[id].replace(/(?<a>[a-z0-9])(?<b>[A-Z])/g, '$1-$2').toLowerCase();

		return classPrefix + formattedId;
	}

	get endpoint() {
		const chartType = this.typeSelector.activeEndpointType;
		const { period, multiplier } = this.periodSelector.activePeriod;
		const { feedUrl, apiVersion, chartName, isin } = this.options;

		return `${feedUrl}/${apiVersion}/${chartName}/${chartType}/${isin}/${multiplier}/${period}`;
	}

	get storageKey() {
		return this.options.storageKey;
	}

	get storedSettings() {
		return storage.get(this.storageKey);
	}
}

export default ChartProducts;
